import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  FileText,
  Plus,
  Edit,
  MoreVertical,
  Calendar,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';
import { procurementService, VendorContract, VendorContractCreate, VendorContractUpdate, ContractType, ContractStatus } from '../../../src/services/procurement.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { authService } from '../../../src/services/auth.service';

interface VendorContractsProps {
  vendorId: string;
  vendorName: string;
}

interface ContractModalProps {
  mode: 'add' | 'edit';
  vendorId: string;
  contract?: VendorContract;
  trigger: React.ReactNode;
  onSuccess: () => void;
}

function ContractModal({ mode, vendorId, contract, trigger, onSuccess }: ContractModalProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contract_number: contract?.contract_number || '',
    contract_name: contract?.contract_name || '',
    contract_type: contract?.contract_type || 'master_agreement' as ContractType,
    status: contract?.status || 'draft' as ContractStatus,
    contract_value: contract?.contract_value?.toString() || '',
    currency: contract?.currency || 'USD',
    effective_date: contract?.effective_date?.split('T')[0] || '',
    expiry_date: contract?.expiry_date?.split('T')[0] || '',
    renewal_date: contract?.renewal_date?.split('T')[0] || '',
    auto_renewal: contract?.auto_renewal || false,
    description: contract?.description || '',
    terms_conditions: contract?.terms_conditions || '',
    payment_terms: contract?.payment_terms || ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const contractTypes: { value: ContractType; label: string }[] = [
    { value: 'master_agreement', label: 'Master Agreement' },
    { value: 'purchase_agreement', label: 'Purchase Agreement' },
    { value: 'service_agreement', label: 'Service Agreement' },
    { value: 'rental_agreement', label: 'Rental Agreement' },
    { value: 'nda', label: 'NDA' },
    { value: 'other', label: 'Other' }
  ];

  const contractStatuses: { value: ContractStatus; label: string }[] = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'terminated', label: 'Terminated' },
    { value: 'pending_renewal', label: 'Pending Renewal' }
  ];

  const paymentTermsOptions = [
    { value: 'cash_on_delivery', label: 'Cash on Delivery' },
    { value: 'advance', label: 'Advance Payment' },
    { value: 'net_10', label: 'Net 10 Days' },
    { value: 'net_15', label: 'Net 15 Days' },
    { value: 'net_30', label: 'Net 30 Days' },
    { value: 'net_45', label: 'Net 45 Days' },
    { value: 'net_60', label: 'Net 60 Days' },
    { value: 'net_90', label: 'Net 90 Days' },
    { value: 'progress_based', label: 'Progress Based' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'upon_completion', label: 'Upon Completion' }
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.contract_number.trim()) {
      newErrors.contract_number = 'Contract number is required';
    }
    if (!formData.contract_name.trim()) {
      newErrors.contract_name = 'Contract name is required';
    }
    if (!formData.effective_date) {
      newErrors.effective_date = 'Effective date is required';
    }
    if (!formData.expiry_date) {
      newErrors.expiry_date = 'Expiry date is required';
    }
    if (formData.effective_date && formData.expiry_date && formData.effective_date >= formData.expiry_date) {
      newErrors.expiry_date = 'Expiry date must be after effective date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in all required fields correctly'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const contractData = {
        contract_number: formData.contract_number,
        contract_name: formData.contract_name,
        contract_type: formData.contract_type,
        status: formData.status,
        contract_value: formData.contract_value ? parseFloat(formData.contract_value) : undefined,
        currency: formData.currency,
        effective_date: formData.effective_date,
        expiry_date: formData.expiry_date,
        renewal_date: formData.renewal_date || undefined,
        auto_renewal: formData.auto_renewal,
        description: formData.description || undefined,
        terms_conditions: formData.terms_conditions || undefined,
        payment_terms: formData.payment_terms || undefined
      };

      const userProfile = await authService.getUserProfile();

      if (mode === 'add') {
        await procurementService.createVendorContract(vendorId, contractData as VendorContractCreate, userProfile.organizationId);
        showSuccess({
          title: 'Contract Created',
          message: `Contract ${formData.contract_number} has been created successfully`
        });
      } else if (mode === 'edit' && contract) {
        await procurementService.updateVendorContract(contract.contract_id, contractData as VendorContractUpdate);
        showSuccess({
          title: 'Contract Updated',
          message: `Contract ${formData.contract_number} has been updated successfully`
        });
      }

      setOpen(false);
      onSuccess();
    } catch (error: any) {
      console.error('Failed to save contract:', error);
      showError({
        title: mode === 'add' ? 'Failed to Create Contract' : 'Failed to Update Contract',
        message: error?.message || 'An unexpected error occurred'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {mode === 'add' ? 'Create New Contract' : 'Edit Contract'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Create a new contract for this vendor' : 'Update contract information'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contract-number">Contract Number *</Label>
              <Input
                id="contract-number"
                value={formData.contract_number}
                onChange={(e) => setFormData({...formData, contract_number: e.target.value})}
                placeholder="CNT-2024-001"
              />
              {errors.contract_number && <p className="text-sm text-destructive">{errors.contract_number}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract-type">Contract Type</Label>
              <Select
                value={formData.contract_type}
                onValueChange={(value) => setFormData({...formData, contract_type: value as ContractType})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {contractTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contract-name">Contract Name *</Label>
            <Input
              id="contract-name"
              value={formData.contract_name}
              onChange={(e) => setFormData({...formData, contract_name: e.target.value})}
              placeholder="Annual Supply Agreement 2024"
            />
            {errors.contract_name && <p className="text-sm text-destructive">{errors.contract_name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({...formData, status: value as ContractStatus})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {contractStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract-value">Contract Value</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({...formData, currency: value})}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  id="contract-value"
                  type="number"
                  value={formData.contract_value}
                  onChange={(e) => setFormData({...formData, contract_value: e.target.value})}
                  placeholder="0.00"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="effective-date">Effective Date *</Label>
              <Input
                id="effective-date"
                type="date"
                value={formData.effective_date}
                onChange={(e) => setFormData({...formData, effective_date: e.target.value})}
              />
              {errors.effective_date && <p className="text-sm text-destructive">{errors.effective_date}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiry-date">Expiry Date *</Label>
              <Input
                id="expiry-date"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
              />
              {errors.expiry_date && <p className="text-sm text-destructive">{errors.expiry_date}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="renewal-date">Renewal Date</Label>
              <Input
                id="renewal-date"
                type="date"
                value={formData.renewal_date}
                onChange={(e) => setFormData({...formData, renewal_date: e.target.value})}
              />
            </div>
            <div className="flex items-center space-x-2 pt-8">
              <input
                type="checkbox"
                id="auto-renewal"
                checked={formData.auto_renewal}
                onChange={(e) => setFormData({...formData, auto_renewal: e.target.checked})}
                className="rounded border-border"
              />
              <Label htmlFor="auto-renewal" className="cursor-pointer">
                Auto-renewal enabled
              </Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Brief description of the contract..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-terms">Payment Terms</Label>
            <Select
              value={formData.payment_terms}
              onValueChange={(value) => setFormData({...formData, payment_terms: value})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select payment terms" />
              </SelectTrigger>
              <SelectContent>
                {paymentTermsOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="terms-conditions">Terms & Conditions</Label>
            <Textarea
              id="terms-conditions"
              value={formData.terms_conditions}
              onChange={(e) => setFormData({...formData, terms_conditions: e.target.value})}
              placeholder="Contract terms and conditions..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : mode === 'add' ? 'Create Contract' : 'Update Contract'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VendorContracts({ vendorId, vendorName }: VendorContractsProps) {
  const [contracts, setContracts] = useState<VendorContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const fetchContracts = async () => {
    try {
      setLoading(true);
      console.log('[VendorContracts] Fetching contracts for vendorId:', vendorId);
      console.log('[VendorContracts] Status filter:', statusFilter);
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const response = await procurementService.getVendorContracts(vendorId, status);
      console.log('[VendorContracts] API Response:', response);
      console.log('[VendorContracts] Response.data:', response.data);
      const contractsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      console.log('[VendorContracts] Contracts loaded:', contractsData.length, contractsData);
      setContracts(contractsData);
    } catch (error) {
      console.error('[VendorContracts] Failed to fetch contracts:', error);
      showError({
        title: 'Failed to Load Contracts',
        message: 'Unable to load vendor contracts'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [vendorId, statusFilter]);

  const getStatusBadge = (status: ContractStatus) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case 'draft':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200"><FileText className="h-3 w-3 mr-1" />Draft</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800 border-red-200"><X className="h-3 w-3 mr-1" />Expired</Badge>;
      case 'terminated':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200"><AlertTriangle className="h-3 w-3 mr-1" />Terminated</Badge>;
      case 'pending_renewal':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><Clock className="h-3 w-3 mr-1" />Pending Renewal</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (value?: number, currency?: string) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading contracts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Contracts</h3>
          <p className="text-sm text-muted-foreground">Manage contracts for {vendorName}</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="pending_renewal">Pending Renewal</SelectItem>
            </SelectContent>
          </Select>
          <ContractModal
            mode="add"
            vendorId={vendorId}
            onSuccess={fetchContracts}
            trigger={
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Contract
              </Button>
            }
          />
        </div>
      </div>

      {contracts.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No contracts yet</h3>
          <p className="text-muted-foreground mb-4">Create contracts to manage agreements with this vendor</p>
          <ContractModal
            mode="add"
            vendorId={vendorId}
            onSuccess={fetchContracts}
            trigger={
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create First Contract
              </Button>
            }
          />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contract</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contracts.map((contract) => (
              <TableRow key={contract.contract_id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{contract.contract_name}</p>
                    <p className="text-sm text-muted-foreground">{contract.contract_number}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{contract.contract_type.replace('_', ' ')}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    {formatCurrency(contract.contract_value, contract.currency)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">Start:</span>
                      {formatDate(contract.effective_date)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">End:</span>
                      {formatDate(contract.expiry_date)}
                      {contract.expires_soon && (
                        <Badge variant="outline" className="text-orange-600 border-orange-600 ml-2">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Expiring Soon
                        </Badge>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(contract.status)}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <ContractModal
                        mode="edit"
                        vendorId={vendorId}
                        contract={contract}
                        onSuccess={fetchContracts}
                        trigger={
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Contract
                          </DropdownMenuItem>
                        }
                      />
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
